const TOKEN_KEY = 'wawi.token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: { feld: string; problem: string }[],
  ) {
    super(message);
  }
}

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Rohdaten statt JSON, z. B. eine CSV-Datei oder ein Foto. */
  raw?: { body: BodyInit; contentType: string };
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Gemeinsamer Unterbau aller Aufrufe: hängt das Token an und leitet bei
 * abgelaufener Sitzung zur Anmeldung. Wer fetch direkt nutzt, verliert
 * genau diese Umleitung - deshalb läuft alles hierüber.
 */
async function sende(url: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const response = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401 && !new URL(url, window.location.origin).pathname.endsWith('/login')) {
    clearToken();
    window.location.href = '/login';
    throw new ApiError(401, 'Sitzung abgelaufen');
  }
  return response;
}

/** Fehler aus der Antwort lesen; eine Proxy-Fehlerseite ist kein JSON. */
async function fehlerAus(response: Response, ersatz: string): Promise<ApiError> {
  const text = await response.text();
  let payload: Record<string, unknown> = {};
  try {
    payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    /* Ersatztext genügt */
  }
  return new ApiError(
    response.status,
    typeof payload.error === 'string' ? payload.error : ersatz,
    payload.details as { feld: string; problem: string }[] | undefined,
  );
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const contentType = options.raw ? options.raw.contentType : options.body ? 'application/json' : undefined;
  const response = await sende(url.toString(), {
    method: options.method ?? 'GET',
    headers: {
      ...options.headers,
      ...(contentType ? { 'Content-Type': contentType } : {}),
    },
    body: options.raw ? options.raw.body : options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) throw await fehlerAus(response, 'Anfrage fehlgeschlagen');

  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

/** Binärdaten (Bild, PDF) mit Token laden. */
export async function apiBlob(path: string): Promise<Blob> {
  const response = await sende(path);
  if (!response.ok) throw await fehlerAus(response, 'Datei konnte nicht geladen werden');
  return response.blob();
}

/**
 * Datei-Download mit Token. Ein einfacher Link ginge nicht - die API erwartet
 * den Authorization-Kopf, den der Browser bei einem Link nicht mitschickt.
 */
export async function downloadFile(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown } = {},
): Promise<void> {
  const response = await sende(path, {
    method: options.method ?? 'GET',
    headers: options.body ? { 'Content-Type': 'application/json' } : {},
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) throw await fehlerAus(response, 'Download fehlgeschlagen');

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = match?.[1] ?? 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Öffnet einen Beleg (PDF o. Ä.) in einem neuen Tab - mit Token.
 *
 * Ein einfacher Link ginge nicht, weil die API den Authorization-Kopf
 * erwartet. Das Fenster wird synchron im Klick geöffnet (sonst greift der
 * Popup-Blocker) und danach auf die Blob-Adresse geleitet.
 */
export async function oeffneBeleg(path: string): Promise<void> {
  const fenster = window.open('', '_blank');
  try {
    const url = URL.createObjectURL(await apiBlob(path));
    if (fenster) fenster.location.href = url;
    else window.location.href = url;
    // Der neue Tab hat das PDF dann längst geladen; ohne Freigabe bliebe
    // jeder geöffnete Beleg bis zum Neuladen im Speicher dieses Tabs.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    if (fenster) fenster.close();
    throw error;
  }
}

/** Fehlertext inklusive Feldhinweisen, wie er in der Oberfläche erscheint. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.details?.length) {
      return `${error.message}: ${error.details.map((d) => `${d.feld} - ${d.problem}`).join(', ')}`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}

export const formatQty = (value: number, unit?: string) =>
  `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(value)}${unit ? ` ${unit}` : ''}`;

export const formatMoney = (cents: number | null | undefined) =>
  cents === null || cents === undefined
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

export const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export const formatDate = (value: string | null) => {
  if (!value) return '—';
  // Reine Kalendertage nicht über Date parsen - das würde sie als UTC deuten
  // und in westlichen Zeitzonen auf den Vortag verschieben.
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (plain) return `${plain[3]}.${plain[2]}.${plain[1]}`;
  return new Date(value).toLocaleDateString('de-DE');
};
